// Component usage example:
//
//  <PhoneNumberInput
//          classes={{
//            list: {
//              paper: classes.phoneList,
//              listItem: classes.phoneListItem,
//            },
//          }}
//          listPlacement='top-start'
//         options={state.countryList}
//         numberOptions={[
//           '+124334343434',
//           '+124334343434',
//           '+124334343434',
//           '+124334343434',
//           '+124334343434',
//         ]}
//         multiple
//         onInputChange={(value, error) => {

//         }}
//         onChange={(selectedNumbers, externalNumberError) => {
//           console.log('ON CHANGE', selectedNumbers);
//           setState(prevState => ({
//             ...prevState,
//             selectedNumbers,
//           }));
//         }}
//         selected={state.selectedNumbers}
//         value={state.numberSearch}
//         validations={{
//           [VALIDATIONS.PHONE]: state.countryList,
//           [VALIDATIONS.REQUIRED]: true,
//         }}
//       />

import React from 'react';
import clsx from 'clsx';
import Chip from '@material-ui/core/Chip';
import InputAdornment from '@material-ui/core/InputAdornment';
import Tooltip from '@material-ui/core/Tooltip';

import { Dropdown, Input, OutsideClickTracker } from '@shared/components';
import { SUPPORTED_COUNTRIES } from '@shared/constants';
import { areEqual, validate } from '@utils';

import InfoLightIcon from '@assets/icons/info-light.svg';

import useStyles from './style';

const defaultCountryIso = 'US';
const defaultState = {
  isListOpen: false,
  options: [],
  selectedCountry: SUPPORTED_COUNTRIES.find(
    country => country.isoCode === defaultCountryIso
  ),
};

const getMappedOptions = options => {
  const mappedOptions = [
    ...SUPPORTED_COUNTRIES,
    ...options.filter(
      option =>
        SUPPORTED_COUNTRIES.findIndex(
          supportedCountry => supportedCountry.isoCode === option.isoCode
        ) === -1
    ),
  ].map(option => ({
    label: `${option.name} (${option.isoCode}) +${option.callingCode}`,
    option,
  }));

  return mappedOptions;
};

const validateElement = (value, validations, type = 'text') => {
  let error = null;
  if (validations) {
    Object.keys(validations).forEach(key => {
      const validationResult = validate(value, key, validations[key], type);
      if (!validationResult.isValid && !error) {
        error = validationResult.errorMessage;
        return;
      }
    });
  }

  return error;
};

const noop = () => {};
const PhoneNumberInput = ({
  classes: passedClasses = {},
  className = '',
  disabled = false,
  fullWidth = false,
  label = 'Phone #',
  listPlacement = 'bottom',
  placeholder = 'Select country code and enter #',
  multiple = false,
  numberOptions = [],
  options = [],
  maxSelected = null,
  onChange = noop,
  tooltip = '',
  tooltipPlacement = 'top',
  onInputChange = noop,
  selected = [],
  validations = {},
  value = '',
}) => {
  const classes = useStyles({
    multiple,
    fullWidth,
  });
  const [state, setState] = React.useState({
    ...defaultState,
    options: getMappedOptions(options),
    selectedNumbers: selected.slice(),
  });

  const updateCountrySelection = React.useCallback(
    (value = '') => {
      if (!value) {
        const defaultCountry = SUPPORTED_COUNTRIES.find(
          country => country.isoCode === defaultCountryIso
        );
        setState(prevState => ({
          ...prevState,
          selectedCountry: defaultCountry,
          // error: validateElement(defaultCountry.callingCode),
        }));

        if (multiple) {
          onInputChange(`+${defaultCountry.callingCode}`, 'Invalid value');
        } else {
          onChange(`+${defaultCountry.callingCode}`, 'Invalid value');
        }
        return;
      }

      const maxCountryCodeLength =
        Math.max(
          state.options.map(country => parseInt(country.option.callingCode))
        ).toString().length + 1;
      if (value.length > 1) {
        let country = null;
        if (value.length <= maxCountryCodeLength) {
          country = state.options.find(supportedCountry =>
            supportedCountry.option.callingCode.indexOf('+') !== -1
              ? value.indexOf(supportedCountry.option.callingCode) === 0
              : value.indexOf(`+${supportedCountry.option.callingCode}`) === 0
          );
        }

        if (value.length > maxCountryCodeLength) {
          const selectedCountryCode = value.slice(0, maxCountryCodeLength);
          country = state.options.find(supportedCountry =>
            supportedCountry.option.callingCode.indexOf('+') !== -1
              ? selectedCountryCode.indexOf(
                  supportedCountry.option.callingCode
                ) === 0
              : selectedCountryCode.indexOf(
                  `+${supportedCountry.option.callingCode}`
                ) === 0
          );
        }

        if (country) {
          setState(prevState => ({
            ...prevState,
            selectedCountry: country && country.option,
          }));
        }
      }
    },
    [state.options, onChange]
  );

  React.useEffect(() => {
    if (options.length > 0) {
      setState(prevState => ({
        ...prevState,
        options: getMappedOptions(options),
      }));
    }
  }, [options]);

  React.useEffect(() => {
    // if (value) {
    updateCountrySelection(value);
    // }
  }, [value, updateCountrySelection]);

  const handleCountrySelection = selectedOption => {
    setState(prevState => ({
      ...prevState,
      isListOpen: false,
      selectedCountry: selectedOption.option,
    }));

    if (multiple) {
      onInputChange(`+${selectedOption.option.callingCode}`, 'Invalid value');
    } else {
      onChange(`+${selectedOption.option.callingCode}`, 'Invalid value');
    }
  };

  const handleInputChange = (event, error) => {
    let value = event.currentTarget.value;
    if (multiple) {
      value = value.replace(/[;]/g, ',');
      value = value.replace(/[:]/g, ',');
      if (value.indexOf(',') === -1) {
        updateCountrySelection(value);
        onInputChange(value, error);
      } else {
        let values = value.split(',');
        values = values.filter(value => value);
        let valueError = null;
        values.forEach((value, index) => {
          const validationResult = validateElement(value, validations);
          if (!valueError && validationResult) {
            valueError = {
              validationResult,
              index,
            };
          }
        });
        if (valueError) {
          onInputChange(
            values.join(','),
            valueError.validationResult,
            valueError.index
          );
        } else {
          const newSelectedValues = selected.slice();
          values.forEach(value => {
            if (newSelectedValues.indexOf(value) === -1) {
              newSelectedValues.push(value);
            }
          });

          onChange(newSelectedValues);
          onInputChange('', null, -1);
          updateCountrySelection(
            `+${state.selectedCountry ? state.selectedCountry.callingCode : 1}`
          );
        }
      }
    } else {
      updateCountrySelection(value);
      onChange(value, error);
    }
  };

  const handleMenuClose = () => {
    setState(prevState => ({
      ...prevState,
      isListOpen: false,
    }));
  };

  let inputClasses = {};
  if (Object.keys(passedClasses).length > 0) {
    inputClasses = { ...passedClasses };
    delete inputClasses.wrapper;
    delete inputClasses.list;
  }

  const handleNumberDelete = numberIndex => {
    const newSelectedNumbers = selected.slice();
    newSelectedNumbers.splice(numberIndex, 1);
    onChange(newSelectedNumbers, null);
  };

  // console.log('VALUE', value);
  return (
    <OutsideClickTracker
      className={clsx(className, classes.wrapper, passedClasses.wrapper)}
      onClickAway={handleMenuClose}
      isListening={state.isOpen}
    >
      <div className={clsx('d-flex', passedClasses.wrapper)}>
        <Dropdown
          classes={{
            wrapper: 'flex-1',
          }}
          customToggle={({ anchorRef, onClick: onToggleClick }) => (
            <Input
              inputRef={anchorRef}
              classes={inputClasses}
              fullWidth
              placeholder={placeholder}
              label={label}
              value={value}
              disabled={
                disabled || (maxSelected && maxSelected === selected.length)
              }
              validations={validations}
              onFocus={onToggleClick}
              onChange={handleInputChange}
              onBlur={event => {
                if (multiple) {
                  event.currentTarget.value += ',';
                  handleInputChange(event);
                }
              }}
              onKeyUp={event => {
                if (multiple && event.which === 13) {
                  event.currentTarget.value = `${value},`;
                  handleInputChange(event);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <div className={clsx('pr-1', classes.dropdownWrapper)}>
                      <Dropdown
                        classes={passedClasses.list}
                        label={state.selectedCountry.isoCode}
                        placement={listPlacement}
                        hasEllipses
                        options={state.options}
                        onChange={handleCountrySelection}
                        disablePortal={false}
                        onToggleClick={() => {
                          onToggleClick(false);
                        }}
                      />
                    </div>
                  </InputAdornment>
                ),
                endAdornment: (
                  <span className={classes.chipsWrapper}>
                    {selected.map((selectedNumber, numberIndex) => (
                      <Chip
                        classes={{
                          root: `mt-1 ${numberIndex === selected.length - 1 &&
                            'mb-1'}`,
                        }}
                        size='small'
                        label={selectedNumber}
                        onDelete={() => handleNumberDelete(numberIndex)}
                      />
                    ))}
                  </span>
                ),
              }}
            />
          )}
          disabled={disabled}
          forceUpdate
          onChange={selectedOption => {
            const newSelectedNumbers = selected.slice();
            if (newSelectedNumbers.indexOf(selectedOption) === -1) {
              newSelectedNumbers.push(selectedOption.label);
            }

            onChange(newSelectedNumbers);
            onInputChange('', null, null);
            updateCountrySelection(
              `+${
                state.selectedCountry ? state.selectedCountry.callingCode : 1
              }`
            );
          }}
          options={
            numberOptions !== null
              ? numberOptions.map(number => ({
                  label: number,
                }))
              : numberOptions
          }
          placement={listPlacement}
          disableOptions={numberOptions === null}
        />
        {tooltip && (
          <Tooltip
            className='ml-2'
            title={tooltip}
            placement={tooltipPlacement}
          >
            <img
              src={InfoLightIcon}
              alt='field tooltip'
              className='info-icon'
            />
          </Tooltip>
        )}
      </div>
    </OutsideClickTracker>
  );
};

export default React.memo(PhoneNumberInput, (prevProps, nextProps) => {
  return (
    areEqual(prevProps.classes, nextProps.classes) &&
    prevProps.className === nextProps.className &&
    prevProps.label === nextProps.label &&
    prevProps.placeholder === nextProps.placeholder &&
    areEqual(prevProps.options, nextProps.options) &&
    prevProps.value === nextProps.value &&
    areEqual(prevProps.validations, nextProps.validations) &&
    areEqual(prevProps.selected, nextProps.selected) &&
    areEqual(prevProps.numberOptions, nextProps.numberOptions) &&
    prevProps.listPlacement === nextProps.listPlacement
  );
});

// export default PhoneNumberInput;
